import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Loader2, DollarSign, Users, BarChart3 } from 'lucide-react'
import { adminApi, CommissionRecord, RepresentativeSummary } from '../../services/api'

type Tab = 'customers' | 'commissions' | 'summary'

export default function RepresentativeDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<Tab>('customers')
  const [filterYear, setFilterYear] = useState<number>(new Date().getFullYear())
  const [filterMonth, setFilterMonth] = useState<number | undefined>(undefined)

  const { data: rep, isLoading } = useQuery({
    queryKey: ['admin-representative', id],
    queryFn: () => adminApi.getRepresentative(id!).then(r => r.data),
    enabled: !!id,
  })

  const { data: customers } = useQuery({
    queryKey: ['admin-rep-customers', id],
    queryFn: () => adminApi.getRepresentativeCustomers(id!).then(r => r.data),
    enabled: !!id && activeTab === 'customers',
  })

  const { data: commissions } = useQuery({
    queryKey: ['admin-rep-commissions', id, filterYear, filterMonth],
    queryFn: () => adminApi.getRepresentativeCommissions(id!, { year: filterYear, month: filterMonth }).then(r => r.data),
    enabled: !!id && activeTab === 'commissions',
  })

  const { data: summary } = useQuery({
    queryKey: ['admin-rep-summary', id, filterYear, filterMonth],
    queryFn: () => adminApi.getRepresentativeSummary(id!, { year: filterYear, month: filterMonth }).then(r => r.data),
    enabled: !!id && activeTab === 'summary',
  })

  const approveMutation = useMutation({
    mutationFn: (commId: string) => adminApi.approveCommission(id!, commId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-rep-commissions'] }),
  })

  const payMutation = useMutation({
    mutationFn: (commId: string) => adminApi.payCommission(id!, commId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-rep-commissions'] }),
  })

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (!rep) {
    return <p className="text-gray-500 text-center py-8">Przedstawiciel nie znaleziony.</p>
  }

  const tabs: { key: Tab; label: string; icon: typeof Users }[] = [
    { key: 'customers', label: 'Klienci', icon: Users },
    { key: 'commissions', label: 'Prowizje', icon: DollarSign },
    { key: 'summary', label: 'Podsumowanie', icon: BarChart3 },
  ]

  const commissionList = (commissions || []) as CommissionRecord[]
  const summaryData = summary as RepresentativeSummary | undefined

  return (
    <div>
      <button
        onClick={() => navigate('/admin/representatives')}
        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Wróć do listy
      </button>

      {/* Info card */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{rep.name}</h2>
            <p className="text-sm text-gray-500">{rep.email}</p>
            {rep.companyName && <p className="text-sm text-gray-500">{rep.companyName}</p>}
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-blue-600">{(rep.commissionRate * 100).toFixed(0)}%</p>
            <p className="text-xs text-gray-400">Prowizja</p>
          </div>
        </div>
        <div className="flex gap-4 mt-4 text-sm">
          <span className="text-gray-500">Aktywni klienci: <span className="font-medium text-gray-900">{rep.activeCustomersCount}</span></span>
          <span className="text-gray-500">Kody: <span className="font-medium text-gray-900">{rep.totalCodesCount}</span></span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${rep.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {rep.isActive ? 'Aktywny' : 'Nieaktywny'}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Filters for commissions and summary */}
      {(activeTab === 'commissions' || activeTab === 'summary') && (
        <div className="flex gap-3 mb-4">
          <select
            value={filterYear}
            onChange={e => setFilterYear(parseInt(e.target.value))}
            className="input py-1.5 px-3 text-sm"
          >
            {[2024, 2025, 2026, 2027].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select
            value={filterMonth ?? ''}
            onChange={e => setFilterMonth(e.target.value ? parseInt(e.target.value) : undefined)}
            className="input py-1.5 px-3 text-sm"
          >
            <option value="">Cały rok</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
              <option key={m} value={m}>
                {new Date(2000, m - 1).toLocaleDateString('pl-PL', { month: 'long' })}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Customers tab */}
      {activeTab === 'customers' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organizacja</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status subskrypcji</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status powiązania</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Przypisano</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(customers as { id: string; organizationName: string; planName: string; subscriptionStatus: string; isActive: boolean; assignedAt: string }[] || []).map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{c.organizationName}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{c.planName}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{c.subscriptionStatus}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      c.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {c.isActive ? 'Aktywny' : 'Nieaktywny'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(c.assignedAt).toLocaleDateString('pl-PL')}
                  </td>
                </tr>
              ))}
              {(!customers || (customers as unknown[]).length === 0) && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400">Brak klientów</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Commissions tab */}
      {activeTab === 'commissions' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organizacja</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kwota faktury</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stawka</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prowizja</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Akcje</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {commissionList.map((cr) => (
                <tr key={cr.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">{cr.organizationName}</td>
                  <td className="px-4 py-3 text-sm">{cr.invoiceAmount.toFixed(2)} PLN</td>
                  <td className="px-4 py-3 text-sm">{(cr.commissionRate * 100).toFixed(0)}%</td>
                  <td className="px-4 py-3 text-sm font-medium text-green-700">{cr.commissionAmount.toFixed(2)} PLN</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(cr.createdAt).toLocaleDateString('pl-PL')}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      cr.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                      cr.status === 'Approved' ? 'bg-blue-100 text-blue-700' :
                      cr.status === 'Paid' ? 'bg-green-100 text-green-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {cr.status === 'Pending' ? 'Oczekująca' :
                       cr.status === 'Approved' ? 'Zatwierdzona' :
                       cr.status === 'Paid' ? 'Wypłacona' :
                       'Anulowana'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {cr.status === 'Pending' && (
                      <button
                        onClick={() => approveMutation.mutate(cr.id)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Zatwierdź
                      </button>
                    )}
                    {cr.status === 'Approved' && (
                      <button
                        onClick={() => payMutation.mutate(cr.id)}
                        className="text-green-600 hover:text-green-800 text-sm font-medium"
                      >
                        Wypłać
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {commissionList.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400">Brak prowizji</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary tab */}
      {activeTab === 'summary' && summaryData && (
        <div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500 uppercase">Przychód łączny</p>
              <p className="text-xl font-bold text-gray-900">{summaryData.totalRevenue.toFixed(2)} PLN</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500 uppercase">Prowizja łączna</p>
              <p className="text-xl font-bold text-blue-600">{summaryData.totalCommission.toFixed(2)} PLN</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500 uppercase">Oczekująca</p>
              <p className="text-xl font-bold text-yellow-600">{summaryData.pendingCommission.toFixed(2)} PLN</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs text-gray-500 uppercase">Wypłacona</p>
              <p className="text-xl font-bold text-green-600">{summaryData.paidCommission.toFixed(2)} PLN</p>
            </div>
          </div>

          <h3 className="text-sm font-medium text-gray-700 mb-3">Rozliczenie per klient</h3>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organizacja</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Przychód</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prowizja</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Płatności</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {summaryData.customerBreakdown.map((c) => (
                  <tr key={c.organizationId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium">{c.organizationName}</td>
                    <td className="px-4 py-3 text-sm">{c.totalRevenue.toFixed(2)} PLN</td>
                    <td className="px-4 py-3 text-sm font-medium text-green-700">{c.totalCommission.toFixed(2)} PLN</td>
                    <td className="px-4 py-3 text-sm">{c.paymentsCount}</td>
                  </tr>
                ))}
                {summaryData.customerBreakdown.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-400">Brak danych</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
